import { useForm } from 'antd/lib/form/Form';
import { Form } from 'antd';
import { ConsoleDrawer } from '../../../components/drawer';
import { ConsoleForm } from '../../../components/form';
import getFormatMessage from '../../../locales/get-format-message';
import { CommonSelect } from '../../../components/select';
import {
  IAPISupportAccountItem,
  IMoreEffectiveEnvItem,
} from '../../../model/api-modules';
import { EnvSelectInfos, EnvSelector } from './env-selector';
import { useEffect, useState } from 'react';
import { getNeedManageSites, getSiteText } from '../../../lib/multi-site';
import { IRootState } from '../../../store/reducers';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';
import { createSupportAccount } from '../../../api';
import { messageSuccess } from '../../../components/message';
import { wrapRequestArea } from '../../../lib/http/lib';
import { getFingerprint } from './fingerprint';
import { CommonInput, CommonTextArea } from '../../../components/input';
import { shortHash10 } from '../lib/hash';

interface ICreateAccountForm {
  site: string;
  ApplicantUserID: string;
  RequestDurationSec: number;
  ApplicationReason: string;
  EnvSelectInfos: EnvSelectInfos;
}

interface IProps {
  record?: IAPISupportAccountItem;
  onClose?: () => void;
  onSave: () => void;
}

export const ApplyBusinessAccountDrawer = (props: IProps) => {
  const { onClose, onSave, record } = props;
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const [form] = useForm<ICreateAccountForm>();
  const [siteOptions, setSiteOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [site, setSite] = useState<string>();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    if (!userInfo) {
      return;
    }
    const options = getNeedManageSites().map((site) => {
      return { label: getSiteText(site), value: site };
    });
    setSiteOptions(options);
    if (options.length === 1) {
      setSite(options[0].value);
    }

    form.setFieldsValue({
      site: options.length > 1 ? undefined : options[0]?.value,
      ApplicantUserID: userInfo.UserID,
      RequestDurationSec:
        (record?.RequestDurationSec || 0) / 60 / 60 || undefined,
      ApplicationReason: record?.ApplicationReason,
    });
  };

  const submit = async () => {
    if (!userInfo) {
      return false;
    }
    const formInfo = await form.validateFields();
    const dataSyncID = await shortHash10(
      `${formInfo.ApplicantUserID}${new Date().getTime()}`
    );
    const allEnvInfos = formInfo.EnvSelectInfos || [];
    const firstEnvInfo = allEnvInfos.shift();
    if (!firstEnvInfo) {
      return false;
    }
    const effectiveEnvInfo: IMoreEffectiveEnvItem = {
      studioID: firstEnvInfo.studioID,
      gameID: firstEnvInfo.gameID,
      envID: firstEnvInfo.envID,
      moreEnvInfos: allEnvInfos,
    };
    const body = {
      ApplicantUserID: userInfo.UserID,
      RequestDurationSec: formInfo.RequestDurationSec * 60 * 60,
      ApplicationReason: formInfo.ApplicationReason,
      EffectiveEnvInfo: effectiveEnvInfo,
      Fingerprint: getFingerprint(),
      DataSyncID: dataSyncID,
      DataSource: 'support',
    };
    const res = await wrapRequestArea(() => createSupportAccount(body), site);
    if (!res) {
      return false;
    }
    onSave?.();
    messageSuccess(getFormatMessage('operation_success'));
    return true;
  };
  return (
    <ConsoleDrawer title="申请账号" onClose={onClose} onOk={submit}>
      <ConsoleForm form={form} className={styles.form}>
        <Form.Item
          label={getFormatMessage('申请人')}
          name="ApplicantUserID"
          extra={getFormatMessage(
            '申请账号仅限本人使用,请勿将账号及密码告知其他人.'
          )}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <span className={styles.applier}>{userInfo?.UserName}</span>
        </Form.Item>
        <Form.Item
          label={getFormatMessage('业务类型')}
          name="site"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <CommonSelect
            disabled={siteOptions.length === 1}
            options={siteOptions}
            placeholder={getFormatMessage('请选择业务类型')}
            onChange={(site) => {
              setSite(site);
            }}
          />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('生效业务环境')}
          name="EnvSelectInfos"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <EnvSelector site={site} />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('使用时长')}
          name="RequestDurationSec"
          rules={[
            {
              required: true,
              validator: async (_: any, value = '') => {
                if (value < 1 || value > 2160 || isNaN(value)) {
                  throw new Error(
                    '请输入1-2160之间的整数,单次使用时长建议在24小时内'
                  );
                }
              },
            },
          ]}
        >
          <CommonInput
            style={{ width: '100%' }}
            placeholder="请输入1-2160之间的整数,单次使用时长建议在24小时内"
            suffix="小时"
          />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('申请原因')}
          name="ApplicationReason"
          rules={[
            {
              required: true,
              validator: async (_: any, value = '') => {
                if (value?.length < 1 || value?.length > 30) {
                  throw new Error('请输入申请账号的原因，不超过30字');
                }
              },
            },
          ]}
        >
          <CommonTextArea
            placeholder="请输入申请原因"
            minLength={2}
            maxLength={30}
          />
        </Form.Item>
      </ConsoleForm>
    </ConsoleDrawer>
  );
};
